/**
 * Fitxer: AppState
 * Autor: Jordi Riu Amblàs
 * Data: 18/12/2017
 * (c) Ajuntament de Manresa - dic 2017 - Departament de Sistemes d'Informació
 */
import { observable, action } from 'mobx';
import md5 from 'md5';
import AjtAjaxAxios from 'ajt-ajax';
import { AjtAppState } from 'ajt-app';
import { IMapConfig, EmptyMapConfig } from '../interfaces/IMapConfig';
import { IEleccio } from '../interfaces/IEleccio';
import { IUsuari } from '../interfaces/IUsuari';
import Util from '../config/util';
import ol from 'openlayers';
import proj4 from 'proj4';
import OlProjection from 'ol/proj/Projection';
import { register } from 'ol/proj/proj4';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import MapaState from './MapaState';
const pjson = require('../../package.json');

const _entorn = (window.location.href.toLowerCase().lastIndexOf('manresa.cat') < 0 
                 ? (window.location.href.toLowerCase().lastIndexOf('localhost') < 0 
                    ? 'intranet' 
                    : 'local')
                 : 'internet');

export default class AppState extends AjtAppState {
    constructor() {
        super({
            titol: 'Selecció de fanals per les eleccions - Ajuntament de Manresa',
            subtitol: 'versió ' + pjson.version,
            showVersions: true,
            urlBase: '/fanals',
            urlBaseSessApi: (_entorn === 'internet' ? 'https://manresa.cat/webapi/Public/Menus/1.0' : 'http://intranetserver/intranet/public/Menus/1.0/api')
        });
        // Llegim el fitxer de configuració
        const cfgFile = `${process.env.PUBLIC_URL}/config_${_entorn}.json`;
        fetch(cfgFile)
        .catch()
        .then(r => r.status !== 404 ? r.text() : '')
        .then(cfg => {
            try {
                this.mapConfig = (JSON.parse(cfg) as IMapConfig);

                // Si cal posem l'ajuda
                if (this.mapConfig.helpUrl) {
                    this.sessState.recurs.RECURLHELP = this.mapConfig.helpUrl;
                }
        
                // Creem i registrem la projecció
                ol.proj.setProj4(proj4);
                proj4.defs('EPSG:25831', '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
                this.olproj = new OlProjection({ code: 'EPSG:25831', units: 'm', metersPerUnit: 1.0,
                    getPointResolution: function(resolution: number) {
                        return resolution;
                    } 
                });
                register(proj4);
                
                // Mirem si tenim capes WMTS per fer la lectura de les capabilities
                var wmtslys: Array<Promise<any>> = [];
                this.mapConfig.baseLayers.forEach((bl) => {
                    if (bl.type.toLowerCase() === 'wmts') {
                        wmtslys.push(
                            fetch(bl.url)
                            .then(function (response: Response) {
                                return response.text();
                            })
                            .then(function (text: string) {
                                const result = new WMTSCapabilities().read(text);
                                const options = optionsFromCapabilities(result, {
                                    layer: bl.layers,
                                    matrixSet: bl.matrixSet
                                });
                                bl.cap = options;
                            })
                        );
                    }
                });
                this.mapConfig.refLayers.forEach((rl) => {
                    if (rl.type.toLowerCase() === 'wmts') {
                        wmtslys.push(
                            fetch(rl.url)
                            .then(function (response: Response) {
                                return response.text();
                            })
                            .then(function (text: string) {
                                // // Xapuça per els que venen del SPA50 i van cap al SigServer
                                // if (rl.replace){
                                //     const orig: string = rl.url.substring(0, rl.url.indexOf('/wmts'));
                                //     text = Util.replaceAll(text, rl.replace, orig);
                                // }
                                const result = new WMTSCapabilities().read(text);
                                const options = optionsFromCapabilities(result, {
                                    layer: rl.layers,
                                    matrixSet: rl.matrixSet
                                });
                                rl.cap = options;
                            })
                        );
                    }
                });
                if (wmtslys.length > 0) { // Hi ha capes WMTS
                    Promise.allSettled(wmtslys)
                    .then(() => {
                        // Marquem que hem llegit bé la configuració
                        this.configOk = true;
                    });
                } else { // No hi ha capes WMTS
                    // Marquem que hem llegit bé la configuració
                    this.configOk = true;
                }
            } catch (error) {
                console.log('Falta fitxer ' + cfgFile + ' o té un format incorrecte.' + error);
            }
        });
    }

    // La nostra projecció
    public olproj: OlProjection;

    // Request
    public req: AjtAjaxAxios = new AjtAjaxAxios();

    // Les dades de l'elecció actual
    @observable ele: IEleccio = { eleCod: 0, eleDesc: '', dataEle: ''};

    // L'usuari loginat
    @observable usr: IUsuari = { userId: '', partitCod: 0, partitNom: '', partitDesc: '', maxFanals: 0, maxEmpls: 0, passWd: '', sesCod: ''};

    // La configuració del mapa
    public mapConfig: IMapConfig = new EmptyMapConfig();

    // L'entorn (internet, intranet)
    public entorn: string = _entorn;

    // Diu si ja no podem modificar els fanals
    public readOnly: boolean = false;

    // El Mapa State
    public mpState: MapaState;

    // Diu si s'ha llegit la configuració
    @observable configOk: boolean = false;

    // Titol de la finestra
    @observable titol: string = '';

    // Mostra la finestra d'informació
    @observable showInfoDlg: boolean = false;

    // Mostra la finestra d'emplaçaments
    @observable showEmplaDlg: boolean = false;

    // Mostra la finestra de copiar lots o emplaçaments
    @observable showCopyEleAntDlg: boolean = false;

    // Què es mostra la finestra de copiar; 'L' lots o 'E' emplaçaments
    @observable typeCopyEleAntDlg: string = '';

    // Funció que es crida després de copiar
    public callBackEleAntDlg: () => void;

    // Categoria del lot que estem ordenant (si é 0 es tanca el diàleg)
    @observable lotOrdCat: number = 0;

    // Element del menu
    @observable anchorEl: HTMLButtonElement;

    // Eleccions anteriors de les que podem copiar els fanals
    @observable lotsAnt: IEleccio[] = [];

    // Eleccions anteriors de les que podem copiar els emplaçaments
    @observable emplsAnt: IEleccio[] = [];
    
    /**
     * Posa el títol
     */
    @action setTitol() {
        let tit: string = '';
        if (this.ele.eleCod) {
            tit = this.ele.eleDesc + " (" + this.ele.dataEle + ")";
        }
        if (this.usr.partitNom) {
            if (tit) {
                tit += " - ";
            }
            tit += this.usr.partitNom;
        }
        if (this.usr.partitDesc) {
            if (tit) {
                tit += " - ";
            }
            tit += this.usr.partitDesc;
        }
        if (this.readOnly) {
            if (tit) {
                tit += " - ";
            }
            tit += ' SELECCIÓ TANCADA'
        } else {
            if (tit) {
                tit += " - ";
            }
            tit += '(Fanals assignats:' + this.usr.maxFanals + ' Emplaçaments assignats:' + this.usr.maxEmpls + ')';
        }
        this.titol = tit;
    }

    /**
     * Posa l'usuari
     * @param userId Nou usuari
     */
    @action setUsuari(userId: string) {
        if  (!userId || this.usr.userId.toUpperCase() !== userId.toUpperCase()) {
            this.usr.userId = userId;
            this.usr.partitCod = 0;
            this.usr.sesCod = '';
        }
    }

    /**
     * Posa la sessio i el PartitCod
     * @param userId Nova sessio
     * @param partitCod El PARTITCOD
     */
    @action setSessio(sesCod: string, partitCod: number) {
        if  (!sesCod || this.usr.sesCod !== sesCod) {
        }
    }

    /**
     * Posa el password
     * @param userId Nou password
     */
    @action setPassword(passWd: string) {
        if  (!passWd || this.usr.passWd !== passWd) {
            this.usr.passWd = passWd;
        }
    }

    /**
     * Mostar o amagar la finestra d'informació
     * @param show true per mostrar-la i false per amagar-la
     */
    @action showInfo(show: boolean) {
        this.showInfoDlg = show;
    }

    /**
     * Mostar o amagar la finestra d'emplaçaments
     * @param show true per mostrar-la i false per amagar-la
     */
     @action showEmpla(show: boolean) {
        this.showEmplaDlg = show;
    }

    /**
     * Mostar o amagar la finestra de copiar lots o emplaçaments
     * @param show true per mostrar-la i false per amagar-la
     * @param type Que es mostra a la finstra 'L' lots, 'E' emplaçaments
     * @param callback Funció que es crida despreés de fer la còpia
     */
    @action showCopyEleAnt(show: boolean, type: string, callback: () => void) {
        this.showCopyEleAntDlg = show;
        this.typeCopyEleAntDlg = type;
        this.callBackEleAntDlg = callback;
    }

    /**
     * Mostar o amagar la finestra d'ordenar lots
     * @param cat 1 a 5 per mostrar-la i 0 per amagar-la
     */
    @action setLotOrdCat(cat: number) {
        this.lotOrdCat = cat;
    }
    
    /**
     * Fa el login amb l'usuari i password entrats
     */
    @action async doLogin(): Promise<{}> {
        const eleCod: number = this.mapConfig.eleCod ? this.mapConfig.eleCod : 0;
        const xreq: string = Util.getXMLRequest('LogInELE', ['Session', 'Usuari', 'Password', 'EleCod'], ['', Util.encodeHTML(this.usr.userId), md5(this.usr.passWd), eleCod.toString()]);
        const resultat = await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        const xresp: string = Util.getXMLResponse('LogInELE', resultat as string);
        if (xresp) {
            this.usr.sesCod = Util.getBetween('Session>', '</Session', xresp, { pos: 0 });
            this.usr.partitCod = parseInt(Util.getBetween('partitCod>', '</partitCod', xresp, { pos: 0 }));
            this.usr.partitNom = Util.getBetween('partitNom>', '</partitNom', xresp, { pos: 0 });
            this.usr.partitDesc = Util.getBetween('partitDesc>', '</partitDesc', xresp, { pos: 0 });
            if (this.usr.partitDesc.length > 38) {
                this.usr.partitDesc = this.usr.partitDesc.substring(0, 35) + "...";
            }
            this.usr.maxFanals = parseInt(Util.getBetween('maxFanals>', '</maxFanals', xresp, { pos: 0 }));
            this.usr.maxEmpls = parseInt(Util.getBetween('maxEmpla>', '</maxEmpla', xresp, { pos: 0 }));
            this.usr.passWd = '';
            this.ele.eleCod = parseInt(Util.getBetween('eleCod>', '</eleCod', xresp, { pos: 0 }));
            this.ele.eleDesc = Util.getBetween('eleDesc>', '</eleDesc', xresp, { pos: 0 });
            this.ele.dataEle = Util.getBetween('dataEle>', '</dataEle', xresp, { pos: 0 });
            this.readOnly = Util.parseBoolean(Util.getBetween('readOnlyFanals>', '</readOnlyFanals', xresp, { pos: 0 }));
            // if (!this.readOnly) {
                const xreql: string = Util.getXMLRequest('GetEleLotsAnt', ['Session', 'Usuari', 'EleCod'], [this.usr.sesCod, Util.encodeHTML(this.usr.userId), this.ele.eleCod.toString()]);
                const resultatl = await this.req.postRequestRaw(this.mapConfig.wsUrl, xreql, { headers: { 'Content-Type': 'text/xml;' } });
                const xrespl: string = Util.getXMLResponse('GetEleLotsAnt', resultatl as string);
                if (xrespl) {
                    let posl: { pos: number } = { pos: 0 };
                    let xelel: string = Util.getBetween('eleccio>', '</eleccio', xrespl, posl);
                    while (xelel) {
                        const eleCod: number = parseInt(Util.getBetween('eleCod>', '</eleCod', xelel, { pos: 0 }));
                        const eleDesc: string = Util.getBetween('eleDesc>', '</eleDesc', xelel, { pos: 0 });
                        const dataEle: string = Util.getBetween('dataEle>', '</dataEle', xelel, { pos: 0 });
                        this.lotsAnt.push({eleCod, eleDesc,  dataEle});
                        xelel = Util.getBetween('eleccio>', '</eleccio', xrespl, posl);
                    }
                }
                const xreqe: string = Util.getXMLRequest('GetEleEmplsAnt', ['Session', 'Usuari', 'EleCod'], [this.usr.sesCod, Util.encodeHTML(this.usr.userId), this.ele.eleCod.toString()]);
                const resultate = await this.req.postRequestRaw(this.mapConfig.wsUrl, xreqe, { headers: { 'Content-Type': 'text/xml;' } });
                const xrespe: string = Util.getXMLResponse('GetEleEmplsAnt', resultate as string);
                if (xrespe) {
                    let pose: { pos: number } = { pos: 0 };
                    let xelee: string = Util.getBetween('eleccio>', '</eleccio', xrespe, pose);
                    while (xelee) {
                        const eleCod: number = parseInt(Util.getBetween('eleCod>', '</eleCod', xelee, { pos: 0 }));
                        const eleDesc: string = Util.getBetween('eleDesc>', '</eleDesc', xelee, { pos: 0 });
                        const dataEle: string = Util.getBetween('dataEle>', '</dataEle', xelee, { pos: 0 });
                        this.emplsAnt.push({eleCod, eleDesc,  dataEle});
                        xelee = Util.getBetween('eleccio>', '</eleccio', xrespe, pose);
                    }
                }
            // }
            this.setTitol();
            return new Promise<boolean>((resolve) => { resolve(true); });
        }
        else {
            this.usr.sesCod = '';
            this.usr.userId = '';
            this.usr.partitCod = 0;
            this.usr.partitNom = '';
            this.usr.partitDesc = '';
            this.usr.maxFanals = 0;
            this.usr.maxEmpls = 0;
            this.usr.passWd = '';
            this.ele.eleCod = 0;
            this.ele.eleDesc = '';
            this.ele.dataEle = '';
            this.lotsAnt = [];
            this.emplsAnt = [];
            this.setTitol();
            return new Promise<boolean>((resolve, reject) => { reject(resultat); });
        }
    }

    /**
     * Fa el logout
     */
    @action doLogout() {
        this.usr.sesCod = '';
        this.usr.partitCod = 0;
        this.usr.partitNom = '';
        this.usr.partitDesc = '';
        this.usr.maxFanals = 0;
        this.usr.maxEmpls = 0;
        this.usr.passWd = '';
        this.ele.eleCod = 0;
        this.ele.eleDesc = '';
        this.ele.dataEle = '';
        this.lotsAnt = [];
        this.emplsAnt = [];
        this.setTitol();
    }

    /**
     * Guardem l'ordre dels emplaçaments
     */
    @action async setEmplsOrd(empls: string) {
        const xreq: string = Util.getXMLRequest('SetEleEmplsOrd', ['Session', 'Usuari', 'EleCod', 'Emplacaments'], [
            this.usr.sesCod,
            Util.encodeHTML(this.usr.userId),
            this.ele.eleCod.toString(),
            empls
        ]);
        /* const resultat = */ await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        // const xresp: string = Util.getXMLResponse('SetEleEmplsOrd', resultat as string);
    }

    /**
     * Guardem l'ordre dels lots
     */
    @action async setLotsOrd(cat: number, lots: string, senseOrdre: boolean) {
        const xreq: string = Util.getXMLRequest('SetEleLotsOrd', ['Session', 'Usuari', 'EleCod', 'Cat', 'Lots', 'SenseOrdre'], [
            this.usr.sesCod,
            Util.encodeHTML(this.usr.userId),
            this.ele.eleCod.toString(),
            cat.toString(),
            lots,
            senseOrdre.toString()
        ]);
        /* const resultat = */ await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        // const xresp: string = Util.getXMLResponse('SetEleEmplsOrd', resultat as string);
    }

    /**
     * Copiem els lots d'unes eleccions anteriors
     */
    @action async setLotsAnt(eleCodAnt: number) {
        const xreq: string = Util.getXMLRequest('SetLotsAnt', ['Session', 'Usuari', 'EleCod', 'EleCodAnt'], [
            this.usr.sesCod,
            Util.encodeHTML(this.usr.userId),
            this.ele.eleCod.toString(),
            eleCodAnt.toString()
        ]);
        /* const resultat = */ await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        // const xresp: string = Util.getXMLResponse('SetEleEmplsOrd', resultat as string);
    }

    /**
     * Copiem els emplaçaments d'unes eleccions anteriors
     */
    @action async setEmplsAnt(eleCodAnt: number) {
        const xreq: string = Util.getXMLRequest('SetEmplsAnt', ['Session', 'Usuari', 'EleCod', 'EleCodAnt'], [
            this.usr.sesCod,
            Util.encodeHTML(this.usr.userId),
            this.ele.eleCod.toString(),
            eleCodAnt.toString()
        ]);
        /* const resultat = */ await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        // const xresp: string = Util.getXMLResponse('SetEleEmplsOrd', resultat as string);
    }

    // Posa o treu l'element del menú
    @action setAnchorEl(anchor: HTMLButtonElement) {
        this.anchorEl = anchor;
    }
    
}